/**
 * @author Theodore Kruczek
 * @description Orbital Object ToolKit (ootk) is a collection of tools for working
 * with satellites and other orbital objects.
 * @license AGPL-3.0-or-later
 * @copyright (c) 2025 Kruczek Labs LLC
 *
 * Many of the classes are based off of the work of @david-rc-dayton and his
 * Pious Squid library (https://github.com/david-rc-dayton/pious_squid) which
 * is licensed under the MIT license.
 *
 * Orbital Object ToolKit is free software: you can redistribute it and/or modify it under the
 * terms of the GNU Affero General Public License as published by the Free Software
 * Foundation, either version 3 of the License, or (at your option) any later version.
 *
 * Orbital Object ToolKit is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License along with
 * Orbital Object ToolKit. If not, see <http://www.gnu.org/licenses/>.
 */
/* eslint-disable no-undefined */
import { ITRF } from '../coordinate/ITRF';
import { AngularDistanceMethod } from '../enums/AngularDistanceMethod';
import { Vector3D } from '../operations/Vector3D';
import { DEG2RAD, halfPi, RAD2DEG, TAU } from '../utils/constants';
import { angularDistance } from '../utils/functions';
// / Range, azimuth, and elevation.
export class RAE {
    epoch;
    rng;
    azRad;
    elRad;
    rngRate;
    azRateRad;
    elRateRad;
    constructor(epoch, rng, azRad, elRad, 
    /** The range rate of the satellite relative to the observer in kilometers per second. */
    rngRate, 
    /** The azimuth rate of the satellite relative to the observer in radians per second. */
    azRateRad, 
    /** The elevation rate of the satellite relative to the observer in radians per second. */
    elRateRad) {
        this.epoch = epoch;
        this.rng = rng;
        this.azRad = azRad;
        this.elRad = elRad;
        this.rngRate = rngRate;
        this.azRateRad = azRateRad;
        this.elRateRad = elRateRad;
        // Do nothing
    }
    // / Create a new [Razel] object, using degrees for the angular values.
    static fromDegrees(epoch, range, azimuth, elevation, rangeRate, azimuthRate, elevationRate) {
        const azimuthRateRad = azimuthRate ? azimuthRate * DEG2RAD : undefined;
        const elevationRateRad = elevationRate ? elevationRate * DEG2RAD : undefined;
        return new RAE(epoch, range, (azimuth * DEG2RAD), (elevation * DEG2RAD), rangeRate, azimuthRateRad, elevationRateRad);
    }
    /**
     * Create a [Razel] object from an inertial [state] and [site] vector.
     * @param state The inertial [state] vector.
     * @param site The observer [site] vector.
     * @returns A new [Razel] object.
     */
    static fromStateVector(state, site) {
        const stateEcef = state.toITRF();
        const siteEcef = site.toITRF();
        const po2 = halfPi;
        const r = stateEcef.position.subtract(siteEcef.position);
        const rDot = stateEcef.velocity;
        const geo = siteEcef.toGeodetic();
        const p = r.rotZ(geo.lon).rotY((po2 - geo.lat));
        const pDot = rDot.rotZ(geo.lon).rotY((po2 - geo.lat));
        const pS = p.x;
        const pE = p.y;
        const pZ = p.z;
        const pSDot = pDot.x;
        const pEDot = pDot.y;
        const pZDot = pDot.z;
        const pMag = p.magnitude();
        const pSEMag = Math.sqrt(pS * pS + pE * pE);
        const elevation = Math.asin(pZ / pMag);
        let azimuth;
        if (elevation !== po2) {
            azimuth = Math.atan2(-pE, pS) + Math.PI;
        }
        else {
            azimuth = Math.atan2(-pEDot, pSDot) + Math.PI;
        }
        const rangeRate = p.dot(pDot) / pMag;
        const azimuthRate = (pSDot * pE - pEDot * pS) / (pS * pS + pE * pE);
        const elevationRate = (pZDot - rangeRate * Math.sin(elevation)) / pSEMag;
        return new RAE(state.epoch, pMag, (azimuth % TAU), elevation, rangeRate, azimuthRate, elevationRate);
    }
    /**
     * Gets the azimuth in degrees.
     * @returns The azimuth in degrees.
     */
    get az() {
        return this.azRad * RAD2DEG;
    }
    /**
     * Gets the elevation angle in degrees.
     * @returns The elevation angle in degrees.
     */
    get el() {
        return this.elRad * RAD2DEG;
    }
    /**
     * Gets the azimuth rate in degrees per second.
     * @returns The azimuth rate in degrees per second, or undefined if it is not available.
     */
    get azRate() {
        return this.azRateRad ? this.azRateRad * RAD2DEG : undefined;
    }
    /**
     * Gets the elevation rate in degrees per second.
     * @returns The elevation rate in degrees per second, or undefined if the elevation rate is not set.
     */
    get elRate() {
        return this.elRateRad ? this.elRateRad * RAD2DEG : undefined;
    }
    toString() {
        return [
            '[RazEl]',
            `  Epoch:     ${this.epoch}`,
            `  Azimuth:   ${this.az.toFixed(4)}°`,
            `  Elevation: ${this.el.toFixed(4)}°`,
            `  Range:     ${this.rng.toFixed(3)} km`,
        ].join('\n');
    }
    /**
     * Return the position relative to the observer [site].
     *
     * An optional azimuth [az] _(rad)_ and elevation [el] _(rad)_ value can be
     * passed to override the values contained in this observation.
     * @param site The observer [site].
     * @param azRad Azimuth _(rad)_.
     * @param elRad Elevation _(rad)_.
     * @returns A [Vector3D] object.
     */
    position(site, azRad, elRad) {
        const ecef = site.toITRF();
        const geo = ecef.toGeodetic();
        const po2 = halfPi;
        const newAz = azRad ?? this.azRad;
        const newEl = elRad ?? this.elRad;
        const sAz = Math.sin(newAz);
        const cAz = Math.cos(newAz);
        const sEl = Math.sin(newEl);
        const cEl = Math.cos(newEl);
        const pSez = new Vector3D((-this.rng * cEl * cAz), (this.rng * cEl * sAz), (this.rng * sEl));
        const rEcef = pSez
            .rotY(-(po2 - geo.lat))
            .rotZ(-geo.lon)
            .add(ecef.position);
        return new ITRF(this.epoch, rEcef, Vector3D.origin).toJ2000().position;
    }
    /**
     * Convert this observation into a [J2000] state vector.
     *
     * This will throw an error if the [rangeRate], [elevationRate], or
     * [azimuthRate] are not defined.
     * @param site The observer [site].
     * @returns A [J2000] state vector.
     */
    toStateVector(site) {
        // If the rates are not defined then assume stationary
        this.rngRate ??= 0;
        this.elRateRad ??= 0;
        this.azRateRad ??= 0;
        const ecef = site.toITRF();
        const geo = ecef.toGeodetic();
        const po2 = halfPi;
        const sAz = Math.sin(this.azRad);
        const cAz = Math.cos(this.azRad);
        const sEl = Math.sin(this.elRad);
        const cEl = Math.cos(this.elRad);
        const pSez = new Vector3D((-this.rng * cEl * cAz), (this.rng * cEl * sAz), (this.rng * sEl));
        const pDotSez = new Vector3D((-this.rngRate * cEl * cAz +
            this.rng * sEl * cAz * this.elRateRad +
            this.rng * cEl * sAz * this.azRateRad), (this.rngRate * cEl * sAz -
            this.rng * sEl * sAz * this.elRateRad +
            this.rng * cEl * cAz * this.azRateRad), (this.rngRate * sEl + this.rng * cEl * this.elRateRad));
        const pEcef = pSez.rotY(-(po2 - geo.lat)).rotZ(-geo.lon);
        const pDotEcef = pDotSez
            .rotY(-(po2 - geo.lat))
            .rotZ(-geo.lon);
        const rEcef = pEcef.add(ecef.position);
        return new ITRF(this.epoch, rEcef, pDotEcef).toJ2000();
    }
    /**
     * Calculate the angular distance _(rad)_ between this and another [Razel]
     * object.
     * @param razel The other [Razel] object.
     * @param method The angular distance method to use.
     * @returns The angular distance _(rad)_.
     */
    angle(razel, method = AngularDistanceMethod.Cosine) {
        return angularDistance(this.azRad, this.elRad, razel.azRad, razel.elRad, method);
    }
    /**
     * Calculate the angular distance _(°)_ between this and another [Razel]
     * object.
     * @param razel The other [Razel] object.
     * @param method The angular distance method to use.
     * @returns The angular distance _(°)_.
     */
    angleDegrees(razel, method = AngularDistanceMethod.Cosine) {
        return this.angle(razel, method) * RAD2DEG;
    }
}
